import React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';

// Layout
import Layout from '../components/Layout';

// Auth pages
import Login from '../pages/Login/Login';
import ForgotPassword from '../pages/Login/ForgotPassword';
import VerifyOtp from '../pages/Login/VerifyOtp';
import ResetPassword from '../pages/Login/ResetPassword';
import SelectTenant from '../pages/SelectTenant/SelectTenant';

// Guest Portal (Public)
import GuestOrders from '../pages/GuestPortal/GuestOrders';
import GuestMenu from '../pages/GuestPortal/GuestMenu';
import GuestQRVerify from '../pages/GuestPortal/GuestQRVerify';
import GuestWebCheckin from '../pages/GuestPortal/GuestWebCheckin';
import GuestBookingEngine from '../pages/GuestPortal/GuestBookingEngine';

// Protected pages
import Dashboard from '../pages/Dashboard/Dashboard';
import Users from '../pages/Users/Users';
import UserDetails from '../pages/UserDetails/UserDetails';
import Permissions from '../pages/Permissions/Permissions';
import SecurityManagement from '../pages/Security/SecurityManagement';
import Hotels from '../pages/Hotels/Hotels';
import HotelSettings from '../pages/HotelSettings/HotelSettings';
import BookingSourcesPage from '../pages/HotelSettings/BookingSourcesPage';
import EmailTemplatesPage from '../pages/HotelSettings/EmailTemplatesPage';
import TaxesPage from '../pages/HotelSettings/TaxesPage';
import PaymentMethodsPage from '../pages/HotelSettings/PaymentMethodsPage';
import CancellationPoliciesPage from '../pages/HotelSettings/CancellationPoliciesPage';
import RoomManagement from '../pages/RoomManagement/RoomManagement';
import RoomDetails from '../pages/RoomManagement/RoomDetails';
import PricingManagement from '../pages/PricingManagement/PricingManagement';
import ReservationManagement from '../pages/ReservationManagement/ReservationManagement';
import Housekeeping from '../pages/Housekeeping/Housekeeping';

import RestaurantManagement from '../pages/Restaurant/RestaurantManagement';
import InventoryManagement from '../pages/Inventory/InventoryManagement';
import FinanceManagement from '../pages/Finance/FinanceManagement';
import StaffManagement from '../pages/Staff/StaffManagement';
import ChannelManagement from '../pages/OTA/ChannelManagement';
import ReportsAnalytics from '../pages/Reports/ReportsAnalytics';
import NotificationSettings from '../pages/Notifications/NotificationSettings';

// Route guards
import ProtectedRoute from './ProtectedRoute';
import RoleRoute from './RoleRoute';

/**
 * AppRoutes - Full application route tree.
 * Combines public auth routes with protected + permission-guarded dashboard routes.
 */
const AppRoutes = () => {
    const currentDomain = window.location.hostname;
    
    // This will match 'guest.yourhotel.com', 'guest-staging.yourhotel.com', 
    // and even 'guest.localhost' if you test locally!
    const isGuestDomain = currentDomain.startsWith('guest');

    if (isGuestDomain) {
        return (
            <Routes>
                <Route path="/verify" element={<GuestQRVerify />} />
                <Route path="/orders" element={<GuestOrders />} />
                <Route path="/checkin/:token/*" element={<GuestWebCheckin />} />
                <Route path="/checkin/:token" element={<GuestWebCheckin />} />
                <Route path="/booking" element={<GuestBookingEngine />} />
                <Route path="/book" element={<GuestBookingEngine />} />
                <Route path="/book-rooms" element={<GuestBookingEngine />} />
                <Route path="/booking/*" element={<GuestBookingEngine />} />
                <Route path="/book-rooms/*" element={<GuestBookingEngine />} />
                <Route path="/" element={<GuestMenu />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        );
    }

    return (
        <Routes>
            {/* Public auth routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-otp" element={<VerifyOtp />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Public independent booking engine routes */}
            <Route path="/booking" element={<GuestBookingEngine />} />
            <Route path="/book" element={<GuestBookingEngine />} />
            <Route path="/book-rooms" element={<GuestBookingEngine />} />
            <Route path="/booking/*" element={<GuestBookingEngine />} />
            <Route path="/book-rooms/*" element={<GuestBookingEngine />} />

            {/* Public fallback routes in case the guest domain check fails */}
            <Route path="/checkin/:token/*" element={<GuestWebCheckin />} />
            <Route path="/checkin/:token" element={<GuestWebCheckin />} />

            {/* Standalone select tenant route (requires authentication but no layout context) */}
            <Route
                path="/select-tenant"
                element={
                    <ProtectedRoute>
                        <SelectTenant />
                    </ProtectedRoute>
                }
            />

            {/* Protected dashboard routes */}
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <Layout />
                    </ProtectedRoute>
                }
            >
                {/* All authenticated users can access dashboard (if they have CAN_VIEW_DASHBOARD) */}
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<RoleRoute permissions={["CAN_VIEW_DASHBOARD"]}><Dashboard /></RoleRoute>} />

                {/* Administration */}
                <Route path="users" element={<RoleRoute permissions={["CAN_MANAGE_USERS"]}><Users /></RoleRoute>} />
                <Route path="users/:id" element={<RoleRoute permissions={["CAN_MANAGE_USERS"]}><UserDetails /></RoleRoute>} />
                <Route path="permissions" element={<RoleRoute permissions={["CAN_MANAGE_ROLES", "CAN_MANAGE_PERMISSIONS"]}><Permissions /></RoleRoute>} />
                <Route path="security" element={<RoleRoute permissions={["CAN_MANAGE_SECURITY"]}><SecurityManagement /></RoleRoute>} />
                <Route path="hotels" element={<RoleRoute permissions={["CAN_MANAGE_HOTELS"]}><Hotels /></RoleRoute>} />

                {/* Property / Settings */}
                <Route path="hotel-settings" element={<RoleRoute permissions={["CAN_MANAGE_SETTINGS"]}><HotelSettings /></RoleRoute>} />
                <Route path="hotel-settings/booking-sources" element={<RoleRoute permissions={["CAN_MANAGE_SETTINGS"]}><BookingSourcesPage /></RoleRoute>} />
                <Route path="hotel-settings/email-templates" element={<RoleRoute permissions={["CAN_MANAGE_SETTINGS"]}><EmailTemplatesPage /></RoleRoute>} />
                <Route path="hotel-settings/taxes" element={<RoleRoute permissions={["CAN_MANAGE_SETTINGS"]}><TaxesPage /></RoleRoute>} />
                <Route path="hotel-settings/payment-methods" element={<RoleRoute permissions={["CAN_MANAGE_SETTINGS"]}><PaymentMethodsPage /></RoleRoute>} />
                <Route path="hotel-settings/cancellation-policies" element={<RoleRoute permissions={["CAN_MANAGE_SETTINGS"]}><CancellationPoliciesPage /></RoleRoute>} />
                <Route path="pricing-management" element={<RoleRoute permissions={["CAN_VIEW_RATES", "CAN_MANAGE_RATES", "CAN_VIEW_ROOMS", "CAN_MANAGE_ROOMS"]}><PricingManagement /></RoleRoute>} />
                <Route path="staff" element={<RoleRoute permissions={["CAN_MANAGE_STAFF"]}><StaffManagement /></RoleRoute>} />
                <Route path="ota" element={<RoleRoute permissions={["CAN_MANAGE_OTA"]}><ChannelManagement /></RoleRoute>} />
                <Route path="notifications" element={<RoleRoute permissions={["CAN_VIEW_LOGS"]}><NotificationSettings /></RoleRoute>} />
                <Route path="rates" element={<Navigate to="/pricing-management" replace />} />
                <Route path="booking-channels" element={<Navigate to="/ota" replace />} />
                <Route path="channel-matrix" element={<Navigate to="/ota" replace />} />

                {/* Operations */}
                <Route path="room-management" element={<RoleRoute permissions={["CAN_VIEW_ROOMS", "CAN_MANAGE_ROOMS"]}><RoomManagement /></RoleRoute>} />
                <Route path="room-management/:id" element={<RoleRoute permissions={["CAN_VIEW_ROOMS", "CAN_MANAGE_ROOMS"]}><RoomDetails /></RoleRoute>} />
                <Route path="reservations" element={<RoleRoute permissions={["CAN_VIEW_BOOKING_CALENDAR", "CAN_VIEW_RESERVATIONS", "CAN_CREATE_BOOKING"]}><ReservationManagement /></RoleRoute>} />
                <Route path="housekeeping" element={<RoleRoute permissions={["CAN_MONITOR_CLEANLINESS", "CAN_VIEW_HOUSEKEEPING", "CAN_VIEW_HS_TASKS", "CAN_COMPLETE_HS_TASK", "CAN_UPDATE_ROOM_STATUS"]}><Housekeeping /></RoleRoute>} />
                <Route path="restaurant" element={<RoleRoute permissions={["CAN_MODIFY_MENU", "CAN_APPROVE_ORDER", "CAN_VIEW_RESTAURANT", "CAN_VIEW_KOT", "CAN_UPDATE_ORDER_STATUS"]}><RestaurantManagement /></RoleRoute>} />
                <Route path="inventory" element={<RoleRoute permissions={["CAN_VIEW_STOCK_DASHBOARD", "CAN_APPROVE_REQUISITION", "CAN_RAISE_STOCK_REQUEST", "CAN_VIEW_INVENTORY"]}><InventoryManagement /></RoleRoute>} />

                {/* Finance */}
                <Route path="finance" element={<RoleRoute permissions={["CAN_VIEW_FINANCIALS", "CAN_APPROVE_EXPENSE", "CAN_VIEW_NIGHT_AUDIT"]}><FinanceManagement /></RoleRoute>} />

                {/* Reports */}
                <Route path="reports" element={<RoleRoute permissions={["CAN_VIEW_REPORTS", "CAN_EXPORT_REPORTS"]}><ReportsAnalytics /></RoleRoute>} />
            </Route>
        </Routes>
    );
};

export default AppRoutes;
